/**
 * Per-role home dashboard. Widget set is driven by `widgetsByRole`; the
 * map below is the single source of truth (synced with docs/frontend/ui-spec.md
 * §5.4). `worker` is redirected to /field by the auth guard and never
 * reaches this page.
 */
import { useTranslation } from "react-i18next";
import { AppShell } from "@/layout/AppShell";
import { PendingApprovalsWidget } from "@/widgets/PendingApprovalsWidget";
import { TopRisksWidget } from "@/widgets/TopRisksWidget";
import { LoadSummaryWidget } from "@/widgets/LoadSummaryWidget";
import { RecentChangesWidget } from "@/widgets/RecentChangesWidget";
import { PortfolioOverviewWidget } from "@/widgets/PortfolioOverviewWidget";
import { isProjectManager, useSession, type UserRole } from "@/store/session";

type WidgetKey =
  | "pending_approvals"
  | "top_risks"
  | "load_summary"
  | "recent_changes"
  | "portfolio_overview";

const WIDGET_COMPONENT: Record<WidgetKey, () => React.ReactElement> = {
  pending_approvals: PendingApprovalsWidget,
  top_risks: TopRisksWidget,
  load_summary: LoadSummaryWidget,
  recent_changes: RecentChangesWidget,
  portfolio_overview: PortfolioOverviewWidget,
};

const WIDGETS_BY_ROLE: Partial<Record<UserRole, WidgetKey[]>> = {
  admin: ["pending_approvals", "top_risks", "load_summary", "recent_changes", "portfolio_overview"],
  viewer: ["top_risks", "load_summary", "recent_changes", "portfolio_overview"],
};

export function HomePage(): React.ReactElement {
  const { t } = useTranslation();
  const user = useSession((s) => s.user);
  const roleWidgets: WidgetKey[] = isProjectManager(user)
    ? ["pending_approvals", "top_risks", "load_summary", "recent_changes", "portfolio_overview"]
    : user?.role
      ? (WIDGETS_BY_ROLE[user.role] ?? [])
      : [];
  const widgets = roleWidgets.filter((key) => key !== "recent_changes" || user?.role === "admin");

  return (
    <AppShell>
      <h1 className="text-2xl font-semibold">{t("home.title")}</h1>
      <p className="text-muted-foreground mt-1 text-sm">{t("home.subtitle")}</p>

      {widgets.length === 0 ? (
        <p className="text-muted-foreground mt-6 text-sm" data-testid="home-empty">
          {t("home.widgets.empty")}
        </p>
      ) : (
        <div
          className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
          data-testid="home-widgets"
        >
          {widgets.map((key) => {
            const Component = WIDGET_COMPONENT[key];
            return <Component key={key} />;
          })}
        </div>
      )}
    </AppShell>
  );
}
